import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { PlannedItemLine, VisitTaskChange } from '@jms/model';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { resolveItemFromParams } from '@workbuddy/entities/shared/items';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { isPlannedItemVisitChangeStruck, plannedItemVisitChangeLabel, plannedItemVisitChangeStyle } from '../planned.item.visit.change.tone';
import { plannedItemNameCellBridgeStyles, plannedItemNameCellStyleId } from './planned.item.name.cell.renderer.component.theme';

/**
 * The Item cell on a planned-item board: the shared name-over-location cell with this Visit's change
 * to the line pilled beside the name.
 *
 * A pill rather than a column, by decision. Change state is per-row and usually absent — most lines on
 * a Visit are inherited untouched — so a column would spend width on empty cells to say nothing, while
 * the rows that DID change are exactly the ones a planner is scanning the name column for.
 *
 * A copy of the shared cell rather than a wrapper: the shared one is a leaf renderer with no content
 * slot, and every board that uses it must keep reading identically, so the composition happens here
 * instead of by growing an input that only this entity would ever set.
 *
 * Only a Visit mount ever supplies `visitChange`; a standing board's rows carry none and this renders
 * exactly as the shared cell does.
 */
@Component({
  selector: 'planned-item-name-cell-renderer',
  standalone: true,
  imports: [BadgeComponent],
  template: `
    <div class="name-cell">
      <div class="name-line">
        <span class="item-name" [class.excluded]="$struck()">{{ $name() }}</span>
        @if ($change(); as change) {
          <w-badge
            class="visit-change"
            [label]="change"
            [color]="$changeStyle()?.color"
            [backgroundColor]="$changeStyle()?.backgroundColor"
            [borderColor]="$changeStyle()?.borderColor"
            size="xs"
          />
        }
      </div>
      @if ($location()) {
        <span class="item-location">{{ $location() }}</span>
      }
    </div>
  `,
  styleUrls: ['./planned.item.name.cell.renderer.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlannedItemNameCellRendererComponent implements ICellRendererAngularComp {

  // Tenant-wide, not per-board: `items.hideLocation` suppresses item location everywhere, and this
  // cell is location's only column on a planned-item board.
  private readonly showLocation = !inject(GeneralSettingService).generalSettings?.items?.hideLocation;

  protected readonly $name = signal<string>('');
  protected readonly $location = signal<string>('');
  protected readonly $visitChange = signal<VisitTaskChange | null>(null);

  protected readonly $change = computed(() => plannedItemVisitChangeLabel(this.$visitChange()) || null);
  protected readonly $changeStyle = computed(() => plannedItemVisitChangeStyle(this.$visitChange()));
  protected readonly $struck = computed(() => isPlannedItemVisitChangeStruck(this.$visitChange()));

  public constructor() {

    DesignSystem.Mount(inject(DOCUMENT), plannedItemNameCellStyleId, plannedItemNameCellBridgeStyles);
  }

  public agInit(params: ICellRendererParams): void {

    this.read(params);
  }

  public refresh(params: ICellRendererParams): boolean {

    this.read(params);
    return true;
  }

  private read(params: ICellRendererParams): void {

    const line = resolveItemFromParams(params) as PlannedItemLine | undefined;
    this.$name.set(line?.name ?? '');
    this.$location.set(this.showLocation ? line?.location ?? '' : '');
    this.$visitChange.set(line?.visitChange ?? null);
  }
}

/**
 * Column value for the Item cell — name, location and change state.
 *
 * All three ride the AG value because AG's row-diff refreshes a cell only when its value moves. An
 * Exclude changes nothing but `visitChange`, so a value carrying only the name would leave the row
 * showing no pill until something else on it happened to change.
 */
export function plannedItemNameCellValue(params: { data?: unknown; colDef?: { field?: string } }): string {

  const line = resolveItemFromParams(params as unknown as ICellRendererParams) as PlannedItemLine | undefined;
  if (!line) return '';

  return `${line.name ?? ''}|${line.location ?? ''}|${line.visitChange ?? ''}`;
}
